/**
 * SectionLoading — a skeleton SHAPED like the content that is arriving.
 *
 * The portal's default loading surface was a centred `<CircularProgress>` on empty
 * ground, ~468 times. That tells a user nothing about what they are waiting for, and
 * the layout jumps when data lands because the spinner occupies none of the eventual
 * geometry. A skeleton in the content's own shape does both jobs.
 *
 * This is the same renderer `QueryState` uses internally — extracted so pages that
 * are not (yet) wrapped in QueryState can still show the designed surface. Prefer
 * `QueryState` when you have a TanStack query: it also gives you the empty and error
 * states. Reach for `SectionLoading` when you only have a boolean.
 *
 *   {isLoading && <SectionLoading variant="table" />}
 *   if (isLoading) return <SectionLoading variant="chart" />;
 *
 * A bare `<CircularProgress>` remains correct for INLINE indeterminate actions — a
 * button spinner — which is why the ratchet's `bareloading` counter excludes any
 * CircularProgress carrying a `size=` prop.
 */
import { Box, Skeleton, Stack } from '@mui/material';

export type SectionLoadingVariant = 'table' | 'form' | 'chart' | 'tiles';

export interface SectionLoadingProps {
  /** Match this to what the section actually renders. */
  variant?: SectionLoadingVariant;
  /** Row count for the table/form shapes. */
  rows?: number;
}

export function SectionLoading({ variant = 'table', rows = 5 }: SectionLoadingProps) {
  if (variant === 'chart') {
    return <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 0, my: 1 }} />;
  }

  if (variant === 'tiles') {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1, py: 1 }}>
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} variant="rectangular" height={64} sx={{ borderRadius: 0 }} />
        ))}
      </Box>
    );
  }

  if (variant === 'form') {
    return (
      <Stack spacing={1.25} sx={{ py: 1 }}>
        {Array.from({ length: rows }, (_, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Skeleton variant="text" width={110} sx={{ flexShrink: 0 }} />
            <Skeleton variant="rectangular" height={24} sx={{ flex: 1, borderRadius: 0 }} />
          </Box>
        ))}
      </Stack>
    );
  }

  // table — a header rule then rows, so the section keeps its geometry while loading
  return (
    <Stack spacing={0.75} sx={{ py: 1 }}>
      <Skeleton variant="rectangular" height={22} sx={{ borderRadius: 0, opacity: 0.7 }} />
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} variant="rectangular" height={20} sx={{ borderRadius: 0 }} />
      ))}
    </Stack>
  );
}

export default SectionLoading;
